import { useState } from 'react';
import {
  ArrowBackIcon,
  Button,
  CancelIcon,
  Card,
  Chip,
  Divider,
  EditIcon,
  Field,
  Input,
  Spinner,
  Text,
  Textarea,
} from '@billda/design-system';
import { format, parseISO } from 'date-fns';
import { PermissionGate } from '../../../../shared/components/gates/PermissionGate';
import { Permissions } from '@billda/permissions-ui';
import { LabelValueRow } from '../LabelValueRow';
import { formatCurrency } from '../../../../shared/utils/currency';
import { useEditAdminDeduction } from '../../hooks/useEditAdminDeduction';
import type { AdminDeductionRow } from '@billda/api';
import { CancelDeductionDialog } from './CancelDeductionDialog';

type PanelView = 'details' | 'edit';

const NOTES_MAX_LENGTH = 1000;

interface DeductionDetailPanelContentProps {
  deduction: AdminDeductionRow;
  onSuccess: (message: string) => void;
}

const statusChipConfig: Record<
  string,
  { color: 'success' | 'warning' | 'error' | 'default'; label: string }
> = {
  Approved: { color: 'success', label: 'Approved' },
  Booked: { color: 'warning', label: 'Booked' },
  Rejected: { color: 'error', label: 'Rejected' },
  Cancelled: { color: 'default', label: 'Cancelled' },
};

function DetailsView({
  deduction,
  onEdit,
  onCancel,
}: {
  deduction: AdminDeductionRow;
  onEdit: () => void;
  onCancel: () => void;
}) {
  const chipConfig = statusChipConfig[deduction.status] ?? {
    color: 'default' as const,
    label: deduction.status,
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-auto p-4">
        <div className="flex flex-col gap-4">
          {/* Description + Amount header */}
          <div>
            <Text variant="body1" className="font-semibold">
              {deduction.description}
            </Text>
            <Text
              variant="h5"
              className="mt-1 font-semibold text-content-error"
            >
              {formatCurrency(deduction.amount)}
            </Text>
          </div>

          <Divider />

          {/* Properties */}
          <LabelValueRow
            label="Reference"
            value={deduction.uniqueReference ?? '—'}
          />
          <LabelValueRow
            label="Status"
            value={
              <Chip
                label={chipConfig.label}
                color={chipConfig.color}
                size="sm"
                className="font-semibold"
              />
            }
          />
          <LabelValueRow
            label="Worker / Gang"
            value={deduction.workerOrGangName ?? '—'}
          />
          <LabelValueRow label="Site" value={deduction.siteName ?? '—'} />
          <LabelValueRow label="Plot" value={deduction.plotReference ?? '—'} />
          <LabelValueRow
            label="Date"
            value={
              deduction.deductionDate
                ? format(parseISO(deduction.deductionDate), 'dd MMM yyyy')
                : '—'
            }
          />

          <Divider />

          <LabelValueRow
            label="Created by"
            value={deduction.createdByName ?? '—'}
          />
          <LabelValueRow
            label="Created"
            value={format(parseISO(deduction.createdAt), 'dd MMM yyyy')}
          />

          {deduction.notes && (
            <>
              <Divider />
              <div>
                <Text
                  variant="caption"
                  color="secondary"
                  className="mb-1 block"
                >
                  Notes
                </Text>
                <Text variant="body2" className="whitespace-pre-wrap">
                  {deduction.notes}
                </Text>
              </div>
            </>
          )}

          {/* Cancellation info for cancelled deductions */}
          {deduction.status === 'Cancelled' && deduction.cancellationNotes && (
            <>
              <Divider />
              <div>
                <Text
                  variant="caption"
                  color="secondary"
                  className="mb-1 block"
                >
                  Cancellation Notes
                </Text>
                <Text variant="body2">{deduction.cancellationNotes}</Text>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Fixed footer with actions */}
      {(deduction.canEdit || deduction.canCancel) && (
        <div className="border-t border-border-default bg-surface-default p-4">
          <div className="flex gap-4">
            {deduction.canEdit && (
              <PermissionGate permission={Permissions.Deductions.Approve}>
                <Button variant="outline" onClick={onEdit} fullWidth>
                  <EditIcon aria-hidden="true" className="size-4" />
                  Edit
                </Button>
              </PermissionGate>
            )}
            {deduction.canCancel && (
              <PermissionGate permission={Permissions.Deductions.Approve}>
                <Button
                  variant="outlineDanger"
                  onClick={onCancel}
                  fullWidth
                  data-testid="cancel-deduction-button"
                >
                  <CancelIcon aria-hidden="true" className="size-4" />
                  Cancel Deduction
                </Button>
              </PermissionGate>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function EditView({
  deduction,
  onBack,
  onSuccess,
}: {
  deduction: AdminDeductionRow;
  onBack: () => void;
  onSuccess: (message: string) => void;
}) {
  const [description, setDescription] = useState(deduction.description);
  const [amount, setAmount] = useState(deduction.amount.toString());
  const [notes, setNotes] = useState(deduction.notes ?? '');

  const { mutate: editDeduction, isPending } = useEditAdminDeduction();

  const parsedAmount = parseFloat(amount);
  const amountNum = isNaN(parsedAmount) ? 0 : parsedAmount;
  const isValid =
    description.trim().length >= 3 &&
    amountNum > 0 &&
    notes.length <= NOTES_MAX_LENGTH;

  const handleSave = () => {
    editDeduction(
      {
        deductionId: deduction.id,
        request: {
          description: description.trim(),
          amount: amountNum,
          notes: notes.trim() || null,
        },
      },
      {
        onSuccess: () => onSuccess('Deduction updated successfully'),
      }
    );
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-auto p-4">
        <div className="flex flex-col gap-4">
          {/* Reference (read-only) */}
          <Text variant="body2" color="secondary">
            {deduction.uniqueReference ?? '—'}
          </Text>

          {/* Editable fields */}
          <Card className="p-4">
            <Text variant="body2" className="mb-4 font-semibold">
              Deduction
            </Text>
            <div className="flex flex-col gap-4">
              <Field label="Name" required hint={`${description.length}/200`}>
                <Input
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  maxLength={200}
                />
              </Field>
              <Field label="Amount" required>
                <Input
                  type="number"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  min={0}
                  step="0.01"
                  startAdornment={<span>£</span>}
                />
              </Field>
              <Field label="Notes" hint={`${notes.length}/${NOTES_MAX_LENGTH}`}>
                <Textarea
                  rows={3}
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  maxLength={NOTES_MAX_LENGTH}
                  placeholder="Office only — this doesn't show in the app"
                />
              </Field>
            </div>
          </Card>
        </div>
      </div>

      {/* Fixed footer */}
      <div className="border-t border-border-default bg-surface-default p-4">
        <div className="flex gap-4">
          <Button
            variant="outline"
            onClick={onBack}
            disabled={isPending}
            fullWidth
          >
            <ArrowBackIcon aria-hidden="true" className="size-4" />
            Back
          </Button>
          <Button
            variant="neutral"
            onClick={handleSave}
            disabled={!isValid || isPending}
            fullWidth
          >
            {isPending && <Spinner size={20} color="inherit" />}
            {isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function DeductionDetailPanelContent({
  deduction,
  onSuccess,
}: DeductionDetailPanelContentProps) {
  const [panelView, setPanelView] = useState<PanelView>('details');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  if (panelView === 'edit') {
    return (
      <EditView
        deduction={deduction}
        onBack={() => setPanelView('details')}
        onSuccess={onSuccess}
      />
    );
  }

  return (
    <>
      <DetailsView
        deduction={deduction}
        onEdit={() => setPanelView('edit')}
        onCancel={() => setCancelDialogOpen(true)}
      />
      <CancelDeductionDialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        deduction={{
          id: deduction.id,
          uniqueReference: deduction.uniqueReference,
          description: deduction.description,
          amount: deduction.amount,
          workerOrGangName: deduction.workerOrGangName,
          status: deduction.status,
        }}
        onSuccess={onSuccess}
      />
    </>
  );
}
